import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { sessionsApi, } from "../api/sessions.api.js";
import { credentialsApi } from "../api/credentials.api.js";
import { EmptyState, ErrorState, SegmentedTabs, SkeletonList, PageHeader, colors, } from "../components/ui/index.js";
import { SessionRow } from "../components/sessions/SessionRow.js";
import { MidnightRunsPanel } from "../components/bookings/MidnightRunsPanel.js";
function formatDateHeading(date) {
    // date is "YYYY-MM-DD" (local)
    const [y, m, d] = date.split("-").map((s) => Number(s));
    if (!y || !m || !d)
        return date;
    const dt = new Date(y, m - 1, d);
    return dt.toLocaleDateString(undefined, {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
    });
}
function groupByDate(sessions) {
    const map = new Map();
    for (const s of sessions) {
        const list = map.get(s.date) ?? [];
        list.push(s);
        map.set(s.date, list);
    }
    for (const list of map.values()) {
        list.sort((a, b) => (a.startTime < b.startTime ? -1 : 1));
    }
    return map;
}
export function SessionsPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const rangeParam = searchParams.get("range");
    const range = rangeParam === "past" ? "past" : rangeParam === "deleted" ? "deleted" : "upcoming";
    const view = searchParams.get("view") === "runs" ? "runs" : "sessions";
    const sessionsQ = useQuery({
        queryKey: ["sessions", range],
        queryFn: () => sessionsApi.list({ range }),
        enabled: view === "sessions",
    });
    // Credentials are only used to enrich rows with a label/chip. If this query
    // errors we intentionally don't block the page — rows just render without a
    // credential rather than failing the whole sessions list.
    const credsQ = useQuery({
        queryKey: ["credentials"],
        queryFn: credentialsApi.list,
    });
    const credentialsById = useMemo(() => {
        const map = new Map();
        for (const c of credsQ.data ?? [])
            map.set(c.id, c);
        return map;
    }, [credsQ.data]);
    const grouped = useMemo(() => {
        if (!sessionsQ.data)
            return null;
        const map = groupByDate(sessionsQ.data);
        const sortedDates = [...map.keys()].sort((a, b) => range === "upcoming" ? (a < b ? -1 : 1) : a < b ? 1 : -1);
        return { map, sortedDates };
    }, [sessionsQ.data, range]);
    const setRange = (next) => {
        const params = new URLSearchParams(searchParams);
        params.delete("view");
        if (next === "upcoming")
            params.delete("range");
        else
            params.set("range", next);
        setSearchParams(params, { replace: true });
    };
    const showRuns = () => {
        const params = new URLSearchParams(searchParams);
        params.set("view", "runs");
        setSearchParams(params, { replace: true });
    };
    // Tab selection: "runs" view wins, otherwise the active session range.
    const activeTab = view === "runs" ? "runs" : range;
    const onTabChange = (key) => {
        if (key === "runs")
            showRuns();
        else
            setRange(key);
    };
    if (view === "sessions" && sessionsQ.isLoading)
        return _jsx(SkeletonList, { rows: 5 });
    if (view === "sessions" && sessionsQ.error)
        return _jsx(ErrorState, { message: "Failed to load sessions" });
    const sessions = sessionsQ.data ?? [];
    return (_jsxs("div", { children: [_jsx(PageHeader, { title: "Sessions", subtitle: "Bookings the bot has scheduled for you \u2014 past, today, and upcoming.", help: _jsxs(_Fragment, { children: [_jsx("h3", { style: { fontSize: "1.1rem", margin: "0 0 8px" }, children: "What a session is" }), _jsxs("p", { style: { margin: "0 0 14px", fontSize: "0.95rem" }, children: [_jsx("strong", { children: "One booking on a specific day at a specific venue." }), " A session can cover one OR multiple consecutive time slots \u2014 for example a 2-hour session = 18:00\u201320:00 at John Orwell."] }), _jsx("h3", { style: { fontSize: "1.1rem", margin: "0 0 8px" }, children: "How it works" }), _jsxs("ul", { style: { margin: 0, paddingLeft: 20, fontSize: "0.95rem" }, children: [_jsx("li", { style: { marginBottom: 6 }, children: "Sessions are auto-generated from your Booking Preferences (every 6 hours)." }), _jsxs("li", { style: { marginBottom: 6 }, children: ["Each session waits until ", _jsx("strong", { children: "BeWell opens the slot" }), " (usually 14 days before, midnight)."] }), _jsxs("li", { style: { marginBottom: 6 }, children: ["Then the bot tries up to ", _jsx("strong", { children: "4 times" }), " with growing delays (5s \u2192 15s \u2192 45s)."] }), _jsxs("li", { children: ["Status moves: ", _jsx("strong", { children: "planned \u2192 booked \u2192 completed" }), " (or ", _jsx("strong", { children: "failed" }), " if all retries exhausted)."] })] }), _jsx("h3", { style: { fontSize: "1.1rem", margin: "14px 0 8px" }, children: "When does my court get booked?" }), _jsxs("ul", { style: { margin: 0, paddingLeft: 20, fontSize: "0.95rem" }, children: [_jsxs("li", { style: { marginBottom: 6 }, children: ["The venue lets anyone book a court exactly ", _jsx("strong", { children: "2 weeks (14 days) before" }), " the session, right at ", _jsx("strong", { children: "midnight" }), "."] }), _jsxs("li", { style: { marginBottom: 6 }, children: ["Your session shows up here a couple of days earlier \u2014 roughly ", _jsx("strong", { children: "2 weeks and a bit ahead" }), " \u2014 already queued and waiting to go."] }), _jsx("li", { style: { marginBottom: 6 }, children: "The moment the slot opens (midnight, 2 weeks before), the bot books your court straight away, so you grab it as early as possible." }), _jsx("li", { children: "If that first try doesn't get in, it keeps trying for about a minute before giving up." })] })] }), children: _jsx(SegmentedTabs, { ariaLabel: "Session view", value: activeTab, onChange: onTabChange, tabs: [
                        { key: "upcoming", label: "Upcoming" },
                        { key: "past", label: "Past" },
                        { key: "deleted", label: "Failed / Deleted" },
                        { key: "runs", label: "Runs" },
                    ] }) }), view === "runs" ? (_jsx(MidnightRunsPanel, {})) : sessions.length === 0 || !grouped ? (_jsx(EmptyState, { message: range === "upcoming"
                    ? "No upcoming sessions. Create a rule to start auto-booking."
                    : range === "deleted"
                        ? "No failed or deleted sessions."
                        : "No past sessions yet." })) : (_jsx("div", { style: { display: "grid", gap: 16 }, children: grouped.sortedDates.map((date) => {
                    const items = grouped.map.get(date) ?? [];
                    return (_jsxs("div", { children: [_jsx("h3", { style: {
                                    margin: "0 0 6px 0",
                                    fontSize: "0.85rem",
                                    color: colors.textSecondary,
                                    fontWeight: 600,
                                }, children: formatDateHeading(date) }), _jsx("div", { style: {
                                    border: `1px solid ${colors.border}`,
                                    borderRadius: 10,
                                    overflow: "hidden",
                                    background: colors.surface,
                                }, children: items.map((session, i) => (_jsx(SessionRow, { session: session, isFirst: i === 0, credential: session.credentialId
                                        ? credentialsById.get(session.credentialId)
                                        : undefined }, session.id))) })] }, date));
                }) }))] }));
}
